import React, { useState } from "react";
import {
    Box,
    Typography,
    Divider,
    Checkbox,
    Button,
    CircularProgress,
    IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { FileUpload } from "./FileUpload";
import { ChecklistComments } from "./ChecklistComments";
import { AttachFile, Edit } from "@mui/icons-material";
import { formatDate } from "../utils";
import { useCapabilities } from "../../hooks/useCapabilities";

interface CheckListProps {
    completedStatus: "completed" | "in_progress" | "not_started";
    title: string;
    owner: string;
    completedDate: string;
    downloadLink: string;
    isRequired: boolean;
    checkListId: string;
    isEvidenceRequired: boolean;
    onSave: () => Promise<void> | void;
    isLoading: boolean;
    onUploadComplete: (context: { isEdit: boolean; markComplete: boolean }) => void;
    fileUploadRef?: React.RefObject<any>;
    checklistType?: "activity" | "stageGate";
    allowAttachmentComplete?: boolean;
    selectedChecklistId: string | null;
    setSelectedChecklistId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
    selectedEditId: string | null;
    setSelectedEditId: React.Dispatch<
        React.SetStateAction<string | null>
    >;
    /** Enables the Attachments/Comments tabbed layout with counts. */
    enableComments?: boolean;
    /** Parent id used as the comment `parent_id` (e.g. stage gate id). */
    parentId?: string;
    /** Comment `object_type` (defaults to CHECK_LIST). */
    commentObjectType?: string;
    /** Comment `comment_topic` (defaults to CHECKLIST_COMMENT_ADDED). */
    commentTopic?: string;
}

export const CheckList: React.FC<CheckListProps> = ({
    completedStatus,
    completedDate,
    title,
    owner,
    checkListId,
    isRequired,
    isEvidenceRequired,
    onSave,
    isLoading = false,
    onUploadComplete,
    fileUploadRef,
    checklistType,
    allowAttachmentComplete = false,
    selectedChecklistId,
    setSelectedChecklistId,
    selectedEditId,
    setSelectedEditId,
    enableComments = false,
    parentId,
    commentObjectType,
    commentTopic,
}) => {

    const { isAllowedToCompleteChecklist } = useCapabilities();

    const isCompleted = completedStatus === "completed";
    const status = isCompleted || selectedChecklistId === checkListId;
    const editStatus = selectedEditId === checkListId;
    const [error, setError] = useState<string>("");

    // Tabbed layout state (used when `enableComments` is set, e.g. stage gates).
    const [activeTab, setActiveTab] = useState<"attachments" | "comments" | null>(null);
    const [attachmentCount, setAttachmentCount] = useState<number>(0);
    const [commentCount, setCommentCount] = useState<number>(0);

    // const [editStatus, setEditStatus] = useState(false);

    const handleSave = async () => {
        await onSave();
        setSelectedEditId(null);
    };

    const handleCheckboxChange = () => {
        setSelectedEditId(checkListId);
        setSelectedChecklistId((prev) =>
            prev === checkListId ? null : checkListId
        );
        // Reveal the attachments editor so evidence can be added before completing.
        if (enableComments) setActiveTab("attachments");
    };

    const canOpenAttachmentEditor = isCompleted || allowAttachmentComplete;
    const canEditAttachments = editStatus || (status && !isCompleted) ;
    const showActionButtons =
        (status && (!isCompleted )) ||
        (editStatus && (isCompleted || allowAttachmentComplete || status));

    const handleAttachmentsTab = () => {
        if (activeTab === "attachments") {
            // Only collapse the panel — keep any pending completion/edit intact
            // (use Cancel to actually discard it).
            setActiveTab(null);
        } else {
            setActiveTab("attachments");
            // Enter attachment edit mode without discarding a pending completion.
            if (
                isAllowedToCompleteChecklist &&
                canOpenAttachmentEditor &&
                selectedChecklistId !== checkListId
            ) {
                setSelectedEditId(checkListId);
            }
        }
    };

    const handleCommentsTab = () => {
        setActiveTab((prev) => (prev === "comments" ? null : "comments"));
    };

    const handleCancel = () => {
        if (fileUploadRef && fileUploadRef.current && fileUploadRef.current.clearFiles) {
            fileUploadRef.current.clearFiles();
        }
        setSelectedEditId(null);
        setSelectedChecklistId(null);
        if (enableComments) setActiveTab(null);
    };


    return (
        <Box
            borderColor={"divider"}
            borderRadius={1}
            px={1}
            py={2}
            sx={{ borderWidth: "1px", borderStyle: "solid" }}
            mb={2}
        >
            <Box display="flex" alignItems="start" width="100%" gap={1}>

                {/* checkbox */}
                {isAllowedToCompleteChecklist && (
                    <Checkbox
                        checked={status}
                        onChange={handleCheckboxChange}
                        disabled={(isCompleted || isLoading) && !editStatus}
                    />
                )}

                {/* Title + owner */}
                <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2">{title}</Typography>

                    <Typography variant="caption" color="text.secondary">
                        {isCompleted
                            ? `Completed by ${owner} on ${formatDate(completedDate)}`
                            : `Owner: ${owner}`}
                    </Typography>

                    {/* Attachments / Comments */}
                    {enableComments ? (
                        <Box display="flex" flexDirection="column" width="100%">
                            {/* Tab row with counts */}
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Button
                                    size="small"
                                    startIcon={<AttachFile fontSize="small" />}
                                    onClick={handleAttachmentsTab}
                                    sx={{
                                        textTransform: "none",
                                        color: activeTab === "attachments" ? "primary.main" : "text.secondary",
                                    }}
                                >
                                    {attachmentCount} Attachments
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                                    onClick={handleCommentsTab}
                                    sx={{
                                        textTransform: "none",
                                        color: activeTab === "comments" ? "primary.main" : "text.secondary",
                                    }}
                                >
                                    {commentCount} Comments
                                </Button>
                            </Box>

                            {/* Attachments panel — kept mounted so the count stays live */}
                            <Box sx={{ display: activeTab === "attachments" ? "block" : "none", mt: 1 }}>
                                <FileUpload
                                    type="checklist"
                                    ref={fileUploadRef}
                                    typeId={checkListId}
                                    listView
                                    isRequired={isEvidenceRequired}
                                    isDisableEdit={!canEditAttachments}
                                    isDisableDelete={!canEditAttachments}
                                    onCountChange={setAttachmentCount}
                                    onUploadComplete={() => onUploadComplete({ isEdit: editStatus, markComplete: status })}
                                />
                                {error && (
                                    <Typography variant="caption" color="error">
                                        {error}
                                    </Typography>
                                )}
                            </Box>

                            {/* Comments panel — kept mounted so the count stays live */}
                            {parentId && (
                                <ChecklistComments
                                    parentId={parentId}
                                    objectId={checkListId}
                                    objectType={commentObjectType}
                                    commentTopic={commentTopic}
                                    expanded={activeTab === "comments"}
                                    onCountChange={setCommentCount}
                                />
                            )}
                        </Box>
                    ) : (
                        <>
                            <FileUpload
                                type="checklist"
                                ref={fileUploadRef}
                                typeId={checkListId}
                                isRequired={isEvidenceRequired}
                                isDisableEdit={!canEditAttachments}
                                isDisableDelete={!canEditAttachments}
                                onUploadComplete={() => onUploadComplete({ isEdit: editStatus, markComplete: status })}
                            />
                            {error && (
                                <Typography variant="caption" color="error">
                                    {error}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {/* Attachment action */}
               {!enableComments && isAllowedToCompleteChecklist && canOpenAttachmentEditor && (
                    <IconButton
                        sx={{ color: "primary.main" }}
                        onClick={() => {
                            setSelectedChecklistId(null);
                            setSelectedEditId((prev) =>
                                prev === checkListId ? null : checkListId
                            );
                        }}
                    >
                        {checklistType === "stageGate" ? (
                            <Edit />
                        ) : (
                            <AttachFile fontSize="small" />
                        )}
                    </IconButton>
                )}
            </Box>


      {/* Save / Update button */}
            {showActionButtons && (
                <Box display="flex" flexDirection="column" width="100%" gap={1}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
                        <Button onClick={handleCancel}> Cancel</Button>
                        <Button
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <CheckCircleOutlineIcon />
                                )
                            }
                            variant="contained"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading
                                ? "Saving..."
                                :  editStatus && !status
                                    ? "Save attachments"
                                : editStatus && status
                                    ? "Save details"
                                    : "Update details"}
                        </Button>
                    </Box>
                </Box>
            )}

        </Box>
    );
};
